import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { timestampToDate } from '../../../../utils/firebase-timestamp-transformation';
import './LeavesTab.scss';

const LeavesTab = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [migratedCount, setMigratedCount] = useState(0);

  useEffect(() => {
    fetchLeaves();
  }, []);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      
      // Access the leaves collection
      const leavesCollectionRef = collection(db, 'leaves');
      const querySnapshot = await getDocs(leavesCollectionRef);
      
      const leavesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setLeaves(leavesList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leaves:', error);
      setLoading(false);
    }
  };

  // Function to find new email for an employee from emailChanges.js
  const findNewEmail = (oldEmail) => {
    const change = emailChanges.find(change => change.oldEmail === oldEmail);
    return change ? change.newEmail : null;
  };

  // Handle migrate all leaves
  const handleMigrateAll = async () => {
    if (!window.confirm(`Are you sure you want to migrate all leaves? This will update email addresses in the leaves collection.`)) {
      return;
    }
    
    try {
      setMigrating(true);
      
      // Find leaves that need migration
      const leavesToMigrate = leaves.filter(leave => {
        // Skip already migrated leaves
        if (leave.migrated) return false;
        
        // Check if empEmail has mapping
        return leave.empEmail && findNewEmail(leave.empEmail);
      });
      
      setProgress({ current: 0, total: leavesToMigrate.length });
      
      // Initialize batch
      let batch = writeBatch(db);
      let batchCount = 0;
      let totalMigrated = 0;
      
      // Process leaves in batches (Firestore has a limit of 500 operations per batch)
      for (let i = 0; i < leavesToMigrate.length; i++) {
        const leave = leavesToMigrate[i];
        const oldEmail = leave.empEmail;
        const newEmail = findNewEmail(oldEmail);
        
        if (newEmail) {
          const leaveRef = doc(db, 'leaves', leave.id);
          
          batch.update(leaveRef, {
            empEmail: newEmail,
            oldEmail: oldEmail,
            migrated: true,
            migratedAt: new Date()
          });
          
          batchCount++;
          totalMigrated++;
          
          // If we've reached the batch limit, commit and start a new batch
          if (batchCount >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        }
        
        // Update progress
        setProgress({ current: i + 1, total: leavesToMigrate.length });
      }
      
      // Commit any remaining updates
      if (batchCount > 0) {
        await batch.commit();
      }
      
      setMigratedCount(totalMigrated);
      alert(`Successfully migrated ${totalMigrated} leave records.`);
      
      // Refresh the list
      fetchLeaves();
      
    } catch (error) {
      console.error('Error migrating leaves:', error);
      alert(`Error migrating leaves: ${error.message}`);
    } finally {
      setMigrating(false);
    }
  };

  const filteredLeaves = searchTerm 
    ? leaves.filter(leave => 
        leave.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leave.empEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leave.empID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leave.leaveType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        leave.status?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : leaves;

  // Format date for display using the utility function
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const convertedDate = timestampToDate(date);
    return convertedDate ? convertedDate.toLocaleDateString() : 'N/A';
  };

  // Count total number of migratable leaves
  const getMigratableCounts = () => {
    const total = leaves.length;
    const alreadyMigrated = leaves.filter(leave => leave.migrated).length;
    const canBeMigrated = leaves.filter(leave => 
      !leave.migrated && 
      leave.empEmail && 
      findNewEmail(leave.empEmail)
    ).length;
    const noMapping = total - alreadyMigrated - canBeMigrated;
    
    return { total, alreadyMigrated, canBeMigrated, noMapping };
  };

  const counts = getMigratableCounts();

  // Format leave duration in days
  const formatLeaveDuration = (days) => {
    if (days === undefined || days === null) return 'N/A';
    return days === 1 ? '1 day' : `${days} days`;
  };

  return (
    <div className="leaves-tab">
      <div className="leaves-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by email, employee ID, leave type, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="refresh-container">
          <button 
            className="refresh-button"
            onClick={fetchLeaves}
            disabled={loading || migrating}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      <div className="migration-control">
        <div className="stats">
          <div className="stat-item">
            <span className="stat-label">Total Leaves:</span>
            <span className="stat-value">{counts.total}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Already Migrated:</span>
            <span className="stat-value">{counts.alreadyMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Ready to Migrate:</span>
            <span className="stat-value">{counts.canBeMigrated}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">No Email Mapping:</span>
            <span className="stat-value">{counts.noMapping}</span>
          </div>
        </div>
        
        <button 
          className="migrate-all-button"
          onClick={handleMigrateAll}
          disabled={loading || migrating || counts.canBeMigrated === 0}
        >
          {migrating ? 'Migrating...' : 'Migrate All Leaves'}
        </button>
      </div>
      
      {migrating && (
        <div className="progress-bar-container">
          <div className="progress-text">
            Migrating {progress.current} of {progress.total} leaves...
          </div>
          <div className="progress-bar-wrapper">
            <div 
              className="progress-bar" 
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      {migratedCount > 0 && !migrating && (
        <div className="success-message">
          Successfully migrated {migratedCount} leave records in the last operation.
        </div>
      )}
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading leave records...</p>
        </div>
      ) : filteredLeaves.length > 0 ? (
        <div className="leaves-table-container">
          <table className="leaves-table">
            <thead>
              <tr>
                <th>Employee Email</th>
                <th>Employee ID</th>
                <th>Leave Type</th>
                <th>Duration</th>
                <th>Period</th>
                <th>Status</th>
                <th>Leave Balance</th>
                <th>Migration Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeaves.map(leave => (
                <tr key={leave.id} className={leave.migrated ? 'migrated-row' : ''}>
                  <td>
                    {leave.empEmail || 'N/A'}
                    {leave.migrated && (
                      <div>
                        <span className="migrated-badge">Migrated</span>
                        <div className="old-email">Old: {leave.oldEmail}</div>
                      </div>
                    )}
                  </td>
                  <td>{leave.empID || 'N/A'}</td>
                  <td>{leave.leaveType || 'N/A'}</td>
                  <td>{formatLeaveDuration(leave.leaveDays)}</td>
                  <td>
                    <div>{formatDate(leave.startDate)}</div>
                    <div>to</div>
                    <div>{formatDate(leave.endDate)}</div>
                  </td>
                  <td>
                    <span className={`leave-status ${leave.status?.toLowerCase()}`}>
                      {leave.status || 'N/A'}
                    </span>
                  </td>
                  <td>
                    {leave.preLeaveBalance !== undefined ? (
                      <div>
                        <div>Balance: {leave.preLeaveBalance}</div>
                        {leave.preLeaveTaken !== undefined && (
                          <div>Taken: {leave.preLeaveTaken}</div>
                        )}
                      </div>
                    ) : 'N/A'}
                  </td>
                  <td>
                    {leave.migrated ? (
                      <span className="already-migrated">Migrated</span>
                    ) : findNewEmail(leave.empEmail) ? (
                      <span className="can-migrate">Ready to Migrate</span>
                    ) : (
                      <span className="no-mapping">No Mapping</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          <p>No leave records found.</p>
        </div>
      )}
    </div>
  );
};

export default LeavesTab; 